import {createBrowserHistory} from 'history';
import {applyMiddleware, createStore} from 'redux';
import {multiClientMiddleware} from 'redux-axios-middleware';
import rootReducer from '../reducers/rootReducer';
import axios from 'axios';
import {API_BASE_URL} from '../../config';
import {logOut, refreshToken} from '../actions/auth/authActions';
import {clearLocalStorage, formatError} from '../../helpers/functions';
import {authPath, errorPath, mainPath, rootMainPath} from '../../routes/paths';

export const api = {
    default: {
        client: axios.create({
            baseURL: API_BASE_URL,
            responseType: 'json'
        })
    },
    second: {
        client: axios.create({
            baseURL: API_BASE_URL,
            responseType: 'json'
        })
    }
};

let isRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = (newToken) => {
    refreshSubscribers.forEach((callback) => callback(newToken));
    refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
    refreshSubscribers.push(callback);
};

const notFoundMessages = ['Не знайдено.', 'Not found.']


export async function refreshTokenAndRetry(originalRequest, dispatch) {
    if (isRefreshing) {
        return new Promise((resolve) => {
            addRefreshSubscriber((newToken) => {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                resolve(axios.request(originalRequest));
            });
        });
    }

    isRefreshing = true;
    try {
        const refreshTokenStore = localStorage.getItem('refreshToken');
        const data = {
            refreshToken: refreshTokenStore,
            // ...(isDev && { isDev: true })
        };

        const response = await axios.post(`${API_BASE_URL}auth/token/refresh`, data, {
            headers: {
                "X-App-Platform": 'web'
            }
        });

        if (response.status === 200 && response.data) {
            const newAccessToken = response.data.accessToken;
            const newRefreshToken = response.data.refreshToken;

            api.default.client.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

            localStorage.setItem('token', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);

            onTokenRefreshed(newAccessToken);
            isRefreshing = false;

            return axios.request(originalRequest);
        } else {
            dispatch(logOut());
            clearLocalStorage();
            history.push(authPath.signIn);
            isRefreshing = false;
            return Promise.reject(new Error('Refresh token invalid'));
        }
    } catch (e) {
        if (e.response?.status === 401) {
            dispatch(logOut());
            clearLocalStorage();
            history.push(authPath.signIn);
        }
        isRefreshing = false;
        return Promise.reject(e);
    }
}


const axiosMiddlewareOptions = {
    interceptors: {
        request: [
            (action, config) => {
                config.headers['accept-language'] = localStorage.getItem('langD') ?? 'uk';
                config.headers['X-App-Platform'] = 'web';
                if (localStorage.token) {
                    let token = localStorage.token;
                    config.headers['Authorization'] = 'Bearer ' + token;
                }
                return config;
            }
        ],
        response: [
            {
                success: ({dispatch}, response) => {
                    return response;
                },
                error: async ({dispatch}, error) => {
                    if (error.code === 'ERR_NETWORK' || error.response?.status === 500) {
                        console.log(error);
                        if (error?.config.headers['Authorization']) {
                            history.push(mainPath.error);
                        } else {
                            history.push(errorPath);
                        }
                        formatError(error.response);
                        return Promise.reject(error);
                    }

                    if (error.response?.status === 401) {
                        const originalRequest = error.config;
                        return refreshTokenAndRetry(originalRequest, dispatch);
                    }

                    if (error.response?.status === 403) {
                        if (error.config?.url?.includes('projects/')) {
                            history.push(mainPath.projects);
                        } else if (error.config?.url?.includes('companies/')) {
                            history.push(mainPath.companies);
                        } else {
                            history.push(rootMainPath);
                        }
                        formatError(error.response);
                        return Promise.reject(error);
                    }

                    if (error.response?.status === 409) {
                        if (notFoundMessages.includes(error.response?.data?.message)) {
                            if (error?.config?.url?.includes('projects/')) {
                                history.push(mainPath.projects);
                            } else if (error.config?.url?.includes('companies/')) {
                                history.push(mainPath.companies);
                            }
                            formatError(error.response);
                            return Promise.reject(error);
                        }
                        formatError(error.response);
                        return Promise.reject(error);

                    }

                    if ([422, 405, 400].includes(error.response?.status)) {
                        formatError(error.response);
                        return Promise.reject(error);
                    }

                    return Promise.reject(error);
                }
            }
        ]
    }
};


export const history = createBrowserHistory();

export const setupStore = () => {

    const createStoreWithMiddleware = applyMiddleware(
        multiClientMiddleware(api, axiosMiddlewareOptions),
    )(createStore);

    const store = createStoreWithMiddleware(
        rootReducer(history),
        {},
        window.__REDUX_DEVTOOLS_EXTENSION__ ? window.__REDUX_DEVTOOLS_EXTENSION__() : (f) => f
    );

    return store;
};
